import { setErrorModal } from '@app/slices/modals';
import { downloadBlob } from '@app/util/download';
import {
  useCenterClassesMapMutation,
  useDownloadLevelOfImprovementPdfMutation,
  useDownloadStudentIdentificationPdfMutation,
  useLevelOfImprovementObtainedStatsMutation,
  usePublicationsMutation,
  useStudentIdentificationStatsMutation,
  useUnitsMutation,
} from '@dashboard/api/identificationStats';
import { useResumeStatsMutation } from '@dashboard/api/resumeStats';
import { IdentificationDataSelectionForm } from '@dashboard/components/IdentificationDataSelectionForm';
import { LevelOfImprovementObtainedStats } from '@dashboard/components/LevelOfImprovementObtainedStats';
import { StudentIdentificationStats } from '@dashboard/components/StudentIdentificationStats';
import { GROUPING_NONE } from '@dashboard/constants/identification';
import {
  selectData,
  setCenterClassesMap,
  setLevelOfImprovementObtainedStats,
  setPublications,
  setSelectedCenterClassesMap,
  setSelectedPublicationsUsedByWorkingGroup,
  setSelectedUnits,
  setShowReport,
  setStudentIdentificationStats,
  setUnits,
} from '@dashboard/slices/identificationStats';
import { selectResume, setResume } from '@dashboard/slices/resumeStats';
import React, { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { DashboardSection, LayoutFilters } from '@dashboard/components';
import ResumeCards from '@dashboard/components/ResumeCards/ResumeCards';

import { selectFilter, shouldApplyFilters } from '@dashboard/slices/filters';

import { useTranslation } from 'react-i18next';

import TabsContainer, {
  TabContent,
} from '@common/components/TabsContainer/TabsContainer';
import { Button, Title } from '@common/components';
import Loader from '../../../_common/components/Loader/Loader';
import IdentificationStatsFiltersResume from './IdentificationStatsFiltersResume';
import IdentificationStatsFilters from './IdentificationStatsFilters';

import viewClasses from './IdentificationStatsView.module.scss';

// let lastStatsResults = null;
let currentIdentificationStatsCall = null;
let currentLevelOfImprovementObtainedStatsCall = null;
let currentCenterClassesCall = null;
let currentPublicationsCall = null;
let currentUnitsCall = null;

const STUDENT_IDENTIFICATION_TAB = 'studentIdentification';
const LEVEL_OF_IMPROVEMENT_OBTAINED_TAB =
  'levelOfImprovementObtainedAndEffectSize';

export default function IdentificationStatsView() {
  const dispatch = useAppDispatch();
  const currentStats = useAppSelector(selectData);
  const currentFilters = useAppSelector(selectFilter);
  const resumeStats = useAppSelector(selectResume);
  const publicationsT = useTranslation('dashboard\\Publications');
  const applyFiltersSignal = useAppSelector(shouldApplyFilters);

  const [publicationOptions, setPublicationOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);

  const { t, i18n } = useTranslation('dashboard\\IdentificationStatsView');

  const [
    fetchStudentIdentificationStats,
    { isLoading: isLoadingStudentIdentificationStats },
  ] = useStudentIdentificationStatsMutation();

  const [
    fetchLevelOfImprovementObtainedStats,
    { isLoading: isLoadingLevelOfImprovementObtainedStats },
  ] = useLevelOfImprovementObtainedStatsMutation();

  const [fetchResumeStats, { isLoading: isLoadingResumeStats }] =
    useResumeStatsMutation();

  const [fetchCenterClassesMap, { isLoading: isLoadingCenterClasses }] =
    useCenterClassesMapMutation();

  const [fetchPublications, { isLoading: isLoadingPublications }] =
    usePublicationsMutation();

  const [fetchUnits, { isLoading: isLoadingUnits }] = useUnitsMutation();

  const [
    fetchStudentIdentificationPdf,
    { isLoading: isLoadingStudentIdentificationPdf },
  ] = useDownloadStudentIdentificationPdfMutation();

  const [
    fetchLevelOfImprovementPdf,
    { isLoading: isLoadingLevelOfImprovementPdf },
  ] = useDownloadLevelOfImprovementPdfMutation();

  const isLoading =
    isLoadingStudentIdentificationStats ||
    isLoadingLevelOfImprovementObtainedStats ||
    isLoadingResumeStats ||
    isLoadingCenterClasses ||
    isLoadingPublications ||
    isLoadingUnits;
  const [loading, setLoading] = useState(isLoading);

  const cancelStudentIdentificationStatsRequest = useCallback(() => {
    if (currentIdentificationStatsCall) currentIdentificationStatsCall.abort();
  }, []);

  const cancelLevelOfImprovementObtainedStatsRequest = useCallback(() => {
    if (currentLevelOfImprovementObtainedStatsCall) {
      currentLevelOfImprovementObtainedStatsCall.abort();
    }
  }, []);

  const cancelCenterClassesRequest = useCallback(() => {
    if (currentCenterClassesCall) currentCenterClassesCall.abort();
  }, []);

  const cancelPublicationsRequest = useCallback(() => {
    if (currentPublicationsCall) currentPublicationsCall.abort();
  }, []);

  const cancelUnitsRequest = useCallback(() => {
    if (currentUnitsCall) currentUnitsCall.abort();
  }, []);

  const fetchAndSetStudentIdentificationStats = useCallback(async () => {
    if (!applyFiltersSignal) return;

    const { projects, centers, from, to } = currentFilters;

    try {
      cancelStudentIdentificationStatsRequest();
      const q = {
        projects,
        centers: currentStats.selectedCenterToShow
          ? [currentStats.selectedCenterToShow.value]
          : centers,
        from,
        to,
        units: currentStats.selectedUnits.map((u) => u.value),
        gender:
          currentStats.selectedGender?.value === 'all'
            ? null
            : currentStats.selectedGender?.value,
        classes: currentStats.selectedClassToShow
          ? [currentStats.selectedClassToShow.value]
          : currentStats.selectedClassIds,
        includeStudentsList: currentStats.isStudentIdentificationReportDetailed,
        groupBy: currentStats.selectedGrouping,
      };
      currentIdentificationStatsCall = fetchStudentIdentificationStats(q);
      const results = await currentIdentificationStatsCall.unwrap();
      dispatch(setStudentIdentificationStats(results.data));
    } catch (ex) {
      if (ex && ex.message === 'Aborted') {
        setLoading(false);
      } else {
        throw ex; // Re-throw the error to be handled by the global error handler
      }
    }
  }, [
    applyFiltersSignal,
    cancelStudentIdentificationStatsRequest,
    currentFilters.projects,
    currentFilters.centers,
    currentFilters.from,
    currentFilters.to,
    currentStats.selectedCenterToShow,
    currentStats.selectedUnits,
    currentStats.selectedGender,
    currentStats.selectedClassToShow,
    currentStats.selectedClassIds,
    currentStats.isStudentIdentificationReportDetailed,
    currentStats.selectedGrouping,
    fetchStudentIdentificationStats,
    dispatch,
  ]);

  const fetchAndSetLevelOfImprovementObtainedStats = useCallback(async () => {
    if (!applyFiltersSignal) return;

    const { projects, centers, from, to } = currentFilters;

    try {
      cancelLevelOfImprovementObtainedStatsRequest();
      const q = {
        projects,
        centers: currentStats.selectedCenterToShow
          ? [currentStats.selectedCenterToShow.value]
          : centers,
        from,
        to,
        publicationsUsedByWorkingGroup:
          currentStats.selectedPublicationsUsedByWorkingGroup.map(
            (p) => p.value,
          ),
        minimumWorkedTimeByWorkingGroup:
          currentStats.minimumWorkedTimeByWorkingGroup,
        units: currentStats.selectedUnits.map((u) => u.value),
        gender:
          currentStats.selectedGender?.value === 'all'
            ? null
            : currentStats.selectedGender?.value,
        classes: currentStats.selectedClassToShow
          ? [currentStats.selectedClassToShow.value]
          : currentStats.selectedClassIds,
        groupBy: currentStats.selectedGrouping,
      };
      currentLevelOfImprovementObtainedStatsCall =
        fetchLevelOfImprovementObtainedStats(q);
      const results = await currentLevelOfImprovementObtainedStatsCall.unwrap();
      dispatch(setLevelOfImprovementObtainedStats(results.data));
    } catch (ex) {
      if (ex && ex.message === 'Aborted') {
        setLoading(false);
      } else {
        throw ex; // Re-throw the error to be handled by the global error handler
      }
    }
  }, [
    applyFiltersSignal,
    cancelLevelOfImprovementObtainedStatsRequest,
    currentFilters.projects,
    currentFilters.centers,
    currentFilters.from,
    currentFilters.to,
    currentStats.selectedCenterToShow,
    currentStats.selectedPublicationsUsedByWorkingGroup,
    currentStats.minimumWorkedTimeByWorkingGroup,
    currentStats.selectedUnits,
    currentStats.selectedGender,
    currentStats.selectedClassToShow,
    currentStats.selectedClassIds,
    currentStats.selectedGrouping,
    fetchStudentIdentificationStats,
    dispatch,
  ]);

  const fetchAndSetCenterClassesMap = useCallback(async () => {
    if (!applyFiltersSignal) return;

    const { projects, centers } = currentFilters;

    try {
      cancelCenterClassesRequest();
      const q = {
        projects,
        centers,
      };
      currentCenterClassesCall = fetchCenterClassesMap(q);
      const results = await currentCenterClassesCall.unwrap();
      dispatch(setCenterClassesMap(results.data));
    } catch (ex) {
      if (ex && ex.message === 'Aborted') {
        setLoading(false);
      }
    }
  }, [
    applyFiltersSignal,
    cancelCenterClassesRequest,
    currentFilters,
    fetchCenterClassesMap,
    dispatch,
  ]);

  const fetchAndSetResumeStats = useCallback(async () => {
    if (!applyFiltersSignal) return;

    const { projects, centers } = currentFilters;

    try {
      const q = {
        projects,
        centers,
      };
      const results = await fetchResumeStats(q).unwrap();
      dispatch(setResume(results.data));
    } catch (ex) {
      if (ex && ex.message === 'Aborted') {
        setLoading(false);
      }
    }
  }, [applyFiltersSignal, fetchResumeStats, dispatch]);

  const fetchAndSetPublications = useCallback(async () => {
    if (!applyFiltersSignal) return;

    const { projects, centers, from, to } = currentFilters;

    try {
      cancelPublicationsRequest();

      if (
        !projects.length ||
        !centers.length ||
        !currentStats.selectedClassIds.length
      ) {
        dispatch(setPublications([]));
        return;
      }

      const q = {
        projects,
        centers,
        from,
        to,
        classes: currentStats.selectedClassIds,
      };
      currentPublicationsCall = fetchPublications(q);
      const results = await currentPublicationsCall.unwrap();
      dispatch(setPublications(results.data));
    } catch (ex) {
      if (ex && ex.message === 'Aborted') {
        setLoading(false);
      }
    }
  }, [
    applyFiltersSignal,
    cancelPublicationsRequest,
    currentFilters,
    currentStats.selectedClassIds,
    fetchPublications,
    dispatch,
  ]);

  const fetchAndSetUnits = useCallback(async () => {
    if (!applyFiltersSignal) return;

    const { projects, centers, from, to } = currentFilters;

    try {
      cancelUnitsRequest();

      if (
        !projects.length ||
        !centers.length ||
        !currentStats.selectedClassIds.length
      ) {
        dispatch(setUnits([]));
        return;
      }

      const q = {
        projects,
        centers,
        from,
        to,
        classes: currentStats.selectedClassIds,
      };
      currentUnitsCall = fetchUnits(q);
      const results = await currentUnitsCall.unwrap();
      dispatch(setUnits(results.data));
    } catch (ex) {
      if (ex && ex.message === 'Aborted') {
        setLoading(false);
      }
    }
  }, [
    applyFiltersSignal,
    cancelUnitsRequest,
    currentFilters,
    currentStats.selectedClassIds,
    fetchUnits,
    dispatch,
  ]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    fetchAndSetResumeStats();
  }, [fetchAndSetResumeStats]);

  useEffect(() => {
    fetchAndSetCenterClassesMap();
  }, [fetchAndSetCenterClassesMap]);

  useEffect(() => {
    fetchAndSetPublications();
  }, [fetchAndSetPublications]);

  useEffect(() => {
    fetchAndSetUnits();
  }, [fetchAndSetUnits]);

  useEffect(() => {
    const options = currentStats.publications
      .map((p) => ({
        label: publicationsT.t(p),
        value: p,
      }))
      .sort((p1, p2) => p1.label.localeCompare(p2.label));

    setPublicationOptions(options);

    // Select all publications by default
    dispatch(setSelectedPublicationsUsedByWorkingGroup(options));
  }, [currentStats.publications, publicationsT.t, dispatch]);

  useEffect(() => {
    const options = currentStats.units
      .map((u) => ({
        label: publicationsT.t(u),
        value: u,
      }))
      .sort((p1, p2) => p1.label.localeCompare(p2.label));

    setUnitOptions(options);

    // Select all units by default
    dispatch(setSelectedUnits(options));
  }, [currentStats.units, publicationsT.t, dispatch]);

  useEffect(() => {
    // Select all center classes by default
    const selectedCenterClassesMap = {};
    Object.entries(currentStats.centerClassesMap).forEach(
      ([centerId, classes]) => {
        selectedCenterClassesMap[centerId] = classes.map((c) => ({
          label: c.className,
          value: c.classId,
        }));
      },
    );

    dispatch(setSelectedCenterClassesMap(selectedCenterClassesMap));
  }, [currentStats.centerClassesMap, dispatch]);

  const handleShowLevelOfImprovementObtainedAndEffectSizeReport =
    useCallback(async () => {
      await fetchAndSetLevelOfImprovementObtainedStats();
    }, [fetchAndSetLevelOfImprovementObtainedStats]);

  const handleShowStudentIdentificationReport = useCallback(async () => {
    await fetchAndSetStudentIdentificationStats();
  }, [fetchAndSetStudentIdentificationStats]);

  const tabs = [];

  if (currentStats.isLevelOfImprovementObtainedReportSelected) {
    tabs.push(
      <TabContent
        key="levelOfImprovementObtainedAndEffectSize"
        tabName={t('levelOfImprovementObtainedAndEffectSize')}
        tabValue={LEVEL_OF_IMPROVEMENT_OBTAINED_TAB}
      >
        <IdentificationDataSelectionForm
          onSubmit={handleShowLevelOfImprovementObtainedAndEffectSizeReport}
        />

        <LevelOfImprovementObtainedStats
          isLoading={isLoading}
          stats={currentStats.levelOfImprovementObtainedStats}
          groupBy={currentStats.selectedGrouping}
        />
      </TabContent>,
    );
  }

  if (currentStats.isStudentIdentificationReportSelected) {
    tabs.push(
      <TabContent
        key="studentIdentification"
        tabName={t('studentIdentification')}
        tabValue={STUDENT_IDENTIFICATION_TAB}
      >
        <IdentificationDataSelectionForm
          onSubmit={handleShowStudentIdentificationReport}
        />

        <StudentIdentificationStats
          isLoading={isLoading}
          stats={currentStats.studentIdentificationStats}
          groupBy={currentStats.selectedGrouping}
          isDetailed={currentStats.isStudentIdentificationReportDetailed}
        />
      </TabContent>,
    );
  }

  const [selectedTab, setSelectedTab] = useState(
    () => tabs[0]?.props.tabValue || '',
  );

  const downloadStudentIdentificationPdf = useCallback(async () => {
    const { projects, centers, from, to } = currentFilters;

    try {
      const q = {
        projects,
        centers: currentStats.selectedCenterToShow
          ? [currentStats.selectedCenterToShow.value]
          : centers,
        from,
        to,
        units: currentStats.selectedUnits.map((u) => u.value),
        gender:
          currentStats.selectedGender?.value === 'all'
            ? null
            : currentStats.selectedGender?.value,
        classes: currentStats.selectedClassToShow
          ? [currentStats.selectedClassToShow.value]
          : currentStats.selectedClassIds,
        includeStudentsList: currentStats.isStudentIdentificationReportDetailed,
        groupBy: currentStats.selectedGrouping,
        lang: i18n.language,
      };
      const blob = await fetchStudentIdentificationPdf(q).unwrap();
      const filename = `student-identification-${
        new Date().toISOString().split('T')[0]
      }.pdf`;
      downloadBlob(blob, filename);
    } catch (e) {
      dispatch(setErrorModal(e.message || e));
    }
  }, [
    currentFilters,
    currentStats.selectedCenterToShow,
    currentStats.selectedClassToShow,
    currentStats.selectedUnits,
    currentStats.selectedGender,
    currentStats.selectedClassIds,
    currentStats.isStudentIdentificationReportDetailed,
    currentStats.selectedGrouping,
    fetchStudentIdentificationPdf,
    i18n.language,
  ]);

  const downloadLevelOfImprovementPdf = useCallback(async () => {
    const { projects, centers, from, to } = currentFilters;

    try {
      const q = {
        projects,
        centers: currentStats.selectedCenterToShow
          ? [currentStats.selectedCenterToShow.value]
          : centers,
        from,
        to,
        units: currentStats.selectedUnits.map((u) => u.value),
        gender:
          currentStats.selectedGender?.value === 'all'
            ? null
            : currentStats.selectedGender?.value,
        classes: currentStats.selectedClassToShow
          ? [currentStats.selectedClassToShow.value]
          : currentStats.selectedClassIds,
        publicationsUsedByWorkingGroup:
          currentStats.selectedPublicationsUsedByWorkingGroup.map(
            (p) => p.value,
          ),
        minimumWorkedTimeByWorkingGroup:
          currentStats.minimumWorkedTimeByWorkingGroup,
        groupBy: currentStats.selectedGrouping,
        lang: i18n.language,
      };
      const blob = await fetchLevelOfImprovementPdf(q).unwrap();
      const filename = `level-of-improvement-${
        new Date().toISOString().split('T')[0]
      }.pdf`;
      downloadBlob(blob, filename);
    } catch (e) {
      dispatch(setErrorModal(e.message || e));
    }
  }, [
    currentFilters,
    currentStats.selectedCenterToShow,
    currentStats.selectedClassToShow,
    currentStats.selectedUnits,
    currentStats.selectedGender,
    currentStats.selectedClassIds,
    currentStats.selectedPublicationsUsedByWorkingGroup,
    currentStats.minimumWorkedTimeByWorkingGroup,
    currentStats.selectedGrouping,
    fetchLevelOfImprovementPdf,
    i18n.language,
  ]);

  const handleClickGeneratePdf = useCallback(async () => {
    if (selectedTab === STUDENT_IDENTIFICATION_TAB) {
      await downloadStudentIdentificationPdf();
    } else if (selectedTab === LEVEL_OF_IMPROVEMENT_OBTAINED_TAB) {
      await downloadLevelOfImprovementPdf();
    }
  }, [
    downloadStudentIdentificationPdf,
    downloadLevelOfImprovementPdf,
    selectedTab,
  ]);

  useEffect(() => {
    if (
      !currentStats.showReport ||
      (currentStats.selectedGrouping !== null &&
        currentStats.selectedGrouping !== GROUPING_NONE)
    ) {
      return;
    }

    if (selectedTab === STUDENT_IDENTIFICATION_TAB) {
      handleShowStudentIdentificationReport();
    } else if (selectedTab === LEVEL_OF_IMPROVEMENT_OBTAINED_TAB) {
      handleShowLevelOfImprovementObtainedAndEffectSizeReport();
    }
  }, [
    currentStats.showReport,
    currentStats.selectedGrouping,
    selectedTab,
    handleShowStudentIdentificationReport,
    handleShowLevelOfImprovementObtainedAndEffectSizeReport,
  ]);

  return (
    <LayoutFilters>
      <section className={viewClasses.dashboard}>
        <ResumeCards resume={resumeStats} />
        <DashboardSection title={t('neurodevelopmentTitle')} withoutSeparator>
          {!currentStats.showReport && (
            <IdentificationStatsFilters
              publicationOptions={publicationOptions}
              unitOptions={unitOptions}
              loading={loading}
            />
          )}
          {currentStats.showReport && (
            <>
              <IdentificationStatsFiltersResume />
              <Button
                variant="outline"
                onClick={() => {
                  dispatch(setShowReport(false));
                }}
                pointer
              >
                {t('editFilters')}
              </Button>

              <TabsContainer
                tabs={tabs}
                activeTab={selectedTab}
                onTabChange={(tabValue) => setSelectedTab(tabValue)}
                customStyle={{ marginTop: '30px' }}
                rightContent={
                  <div className={viewClasses.buttonBar}>
                    <Title type="span">{t('exportFullReportAs')}:</Title>
                    <Button
                      variant="raised"
                      color="white"
                      onClick={handleClickGeneratePdf}
                      disabled={
                        isLoadingStudentIdentificationPdf ||
                        isLoadingLevelOfImprovementPdf
                      }
                      suffix={
                        isLoadingStudentIdentificationPdf ||
                        isLoadingLevelOfImprovementPdf ? (
                          <Loader size="small" color="primary" />
                        ) : null
                      }
                    >
                      PDF
                    </Button>
                  </div>
                }
              />
            </>
          )}
        </DashboardSection>
      </section>
    </LayoutFilters>
  );
}

IdentificationStatsView.propTypes = {};
